import React, {useImperativeHandle, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import DialogContent from 'components/atoms/Dialogs/DialogContent/DialogContent';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import DialogFooter from 'components/molecules/Dialogs/DialogFooter/DialogFooter';
import DialogHeader from 'components/molecules/Dialogs/DialogHeader/DialogHeader';
import Button from 'components/atoms/Buttons/Button/Button';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Typography from 'components/atoms/Text/Typography/Typography';
import Markdown from 'components/atoms/Formatters/Markdown/Markdown';
import Close from '@mui/icons-material/Close';
import PlayArrow from '@mui/icons-material/PlayArrow';
import StyledTourDialog from 'components/organisms/Dialogs/TourDialog/TourDialog.styles';

const TourDialog = React.forwardRef((props, ref) => {
  const {
    title,
    description,
    fields,
    onStart,
    ...innerProps
  } = useComponentProps(props, 'TourDialog', {
    children: ['header', 'content', 'footer']
  });

  const innerRef = useRef(null);
  const formRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const handleSubmit = (values, actions) => {
    actions.setSubmitting(false);
    onStart?.(values);
  };

  const handleCancel = (e) => {
    innerRef.current?.close?.(e, 'cancelButtonClick');
  };

  const handleClose = (e, reason) => {
    if (['escapeKeyDown', 'closeButtonClick', 'cancelButtonClick'].includes(reason)) {
      innerProps.onClose?.(e, reason);
    }
  };

  const handleCloseClick = (e) => {
    innerRef.current?.close?.(e, 'closeButtonClick');
  };

  const handleStartClick = () => {
    formRef.current?.submit();
  };

  const renderButtons = () => {
    if (fields) {
      return <React.Fragment>
        <Button children={'Skip the tour'}
                variant="text"
                startIcon={<Icon icon={Close}/>}
                onClick={handleCancel}/>
        <Button type="submit"
                color="success"
                variant="contained"
                children="Start the tour"
                startIcon={<Icon icon={PlayArrow}/>}
                onClick={handleStartClick}/>
      </React.Fragment>;
    } else {
      return <Button color="primary"
                     variant="contained"
                     children="Close"
                     onClick={handleCloseClick}/>;
    }
  };

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTourDialog ref={innerRef} {...innerProps} onClose={handleClose}>
    <DialogHeader className="TourDialog-header"
                  title={title} />
    <DialogContent className="TourDialog-content">
      {description ?
        <Typography as="div"
                    className="TourDialog-description"
                    variant="body2">
          <Markdown>{description}</Markdown>
        </Typography> : null}
      {fields ? <InlineForm ref={formRef}
                            fields={fields}
                            onSubmit={handleSubmit} /> : null}
    </DialogContent>
    <DialogFooter className="TourDialog-footer"
                  buttons={renderButtons()} />
  </StyledTourDialog>
});

TourDialog.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.any,
  description: PropTypes.string,
  fields: PropTypes.array,
  onStart: PropTypes.func,
  onClose: PropTypes.func
};

export default TourDialog;
